"use client";

import { Coins, House, Storefront, User, type AppIcon } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";

import type { SessionUser } from "../../../lib/auth";

interface Tab {
  href: string;
  label: string;
  icon: AppIcon;
}

function isActive(pathname: string, href: string) {
  return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
}

// App-style bottom tab bar — mobile only (sm:hidden), and on mobile the only
// navigation (the slim app bar on top holds just the logo and the cart). A
// floating card with all four corners rounded: pinned full-width to the bottom edge (inset-x-0 bottom-0), then
// pulled in by margins (mx-3, mb-* also clearing the iOS home indicator via
// safe-area-inset-bottom). Margins rather than inset-x-3/bottom-3 so that if
// the margin rules are ever missing, it falls back to a full-width bar at the
// bottom. The active tab is marked, as in a native tab bar.
export function BottomNav({ user }: { user: SessionUser | null }) {
  const pathname = usePathname();
  const tabs: Tab[] = [
    { href: "/", label: "خانه", icon: House },
    { href: "/buy", label: "طلاونقره", icon: Coins },
    { href: "/store", label: "فروشگاه", icon: Storefront },
    user ? { href: "/app", label: "پروفایل", icon: User } : { href: "/signin", label: "ورود", icon: User },
  ];

  return (
    <nav className="bg-primary/95 border-primary text-primary-foreground fixed inset-x-0 bottom-0 z-20 mx-3 mb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex h-16 items-center justify-around rounded-header border shadow-sm backdrop-blur-sm sm:hidden">
      {tabs.map((tab) => {
        const active = isActive(pathname, tab.href);
        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex flex-1 flex-col items-center gap-0.5 rounded-md px-2 py-1.5 text-caption select-none",
              active ? "text-primary-foreground font-semibold" : "text-primary-foreground/60",
            )}
          >
            <tab.icon size={20} />
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
